import classNames from "classnames";
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  forwardRef,
  useRef,
  useState,
} from "react";
import { CodeInputProps } from "@common/types";
import { normalizeDigitsToEnglish } from "@app/helpers/normalizeDigits";
import { useIsRtl } from "@app/hooks";
import { Loading } from "../loading";
import { Input, getInputClassName } from "./Input";

const KEY_CODE = {
  backspace: 8,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
};

export const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(
  function CodeInput(
    {
      autoFocus = true,
      disabled = false,
      fields = 6,
      className,
      onComplete,
      icon,
      iconPosition,
      type = "number",
      required = false,
      autoWidth,
      error,
      hint,
      label,
      loading,
      title,
      onChange: _onChange,
    },
    ref,
  ) {
    const isRtl = useIsRtl();
    const iRefs = useRef<Array<HTMLInputElement | null>>([]);
    const [values, setValues] = useState<string[]>(() =>
      Array(fields).fill(""),
    );
    const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
      iRefs.current[index] = el;

      if (index !== 0) {
        return;
      }

      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    const triggerChange = (changes: string[]) => {
      const val = changes.join("");
      _onChange?.(val);
      if (val.length >= fields) {
        onComplete?.(val);
      }
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      const index = parseInt(e.target.dataset.id!);
      if (type === "number") {
        const raw = e.target.value;
        const normalized = normalizeDigitsToEnglish(raw);
        e.target.value = normalized.replace(/[^\d]/g, "");
      }
      // this.handleKeys[index] = false;
      if (
        e.target.value === "" ||
        (type === "number" && !e.target.validity.valid)
      ) {
        return;
      }
      let next;

      const value = e.target.value;
      const vals = [...values];
      if (value.length > 1) {
        let nextIndex = value.length + index - 1;
        if (nextIndex >= fields) {
          nextIndex = fields - 1;
        }
        next = iRefs.current[nextIndex];
        const split = value.split("");
        split.forEach((item, i) => {
          const cursor = index + i;
          if (cursor < fields) {
            vals[cursor] = item;
          }
        });
      } else {
        next = iRefs.current[index + 1];
        vals[index] = value;
      }
      setValues(vals);

      if (next) {
        next.focus();
        next.select();
      }

      triggerChange(vals);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      const index = parseInt(e.currentTarget.dataset.id!);
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      const prev = iRefs.current[prevIndex];
      const next = iRefs.current[nextIndex];
      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault();
          const vals = [...values];
          if (values[index]) {
            vals[index] = "";
            setValues(vals);
          } else if (prev) {
            vals[prevIndex] = "";
            prev.focus();
            setValues(vals);
            triggerChange(vals);
          }
          break;
        }
        case KEY_CODE.left:
          e.preventDefault();
          (isRtl ? next : prev)?.focus();
          break;
        case KEY_CODE.right:
          e.preventDefault();
          (isRtl ? prev : next)?.focus();
          break;
        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault();
          break;
        default:
          // this.handleKeys[index] = true;
          break;
      }
    };

    // onKeyUp = e => {
    //   const index = parseInt(e.target.dataset.id);
    //   if (this.handleKeys[index]) {
    //     this.handleKeys[index] = false;
    //     const next = this.iRefs[index + 1];
    //     if (next) {
    //       next.current.focus();
    //     }
    //   }
    // };

    const onFocus = (e: FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    return (
      <Input
        label={label}
        error={error}
        hint={hint}
        loading={loading}
        icon={icon}
        iconPosition={iconPosition}
      >
        <div>
          {title && <p>{title}</p>}
          <div className="flex gap-2 rtl:flex-row-reverse">
            {values.map((value, index) => (
              <input
                type={type === "number" ? "tel" : type}
                pattern={type === "number" ? "[0-9]*" : undefined}
                autoFocus={autoFocus && index === 0}
                className={classNames(
                  !autoWidth && "block w-full",
                  getInputClassName(error ? "danger" : "default"),
                  className,
                  "text-center",
                )}
                key={index}
                data-id={index}
                value={value}
                id={index + ""}
                ref={setInputRef(index)}
                onChange={onChange}
                onKeyDown={onKeyDown}
                // onKeyUp={this.onKeyUp}
                onFocus={onFocus}
                disabled={disabled}
                required={required}
              />
            ))}
          </div>
          {loading && <Loading />}
        </div>
      </Input>
    );
  },
);
